// adminportal/src/components/WorkflowManagerNew.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Play,
  RefreshCw,
  Server,
  Workflow,
  Settings,
  AlertCircle,
  CheckCircle2,
  Copy,
  Tag,
  Zap,
  FileText,
  FolderCog,
  ListPlus,
  Upload,
  Download,
  Clock,
  ExternalLink,
  Shield,
  FileArchive,
  Send,
  X,
  Check,
  ArrowRight,
} from 'lucide-react';
import Button from './Button';
import Card from './Card';
import { workflowsApiUrl, apiUrl } from '../config';

// Types for workflow management
export type StepType = 'COPY' | 'TAG' | 'CUSTOM' | 'DELETE' | 'DECRYPT';
export type UiStep = {
  id: string;
  type: StepType;
  category: 'nominal' | 'exception';
  name?: string;
  sourceFileLocation?: string;
  destinationBucket?: string;
  destinationKey?: string;
  tags?: { key: string; value: string }[];
  functionArn?: string;
  invocationType?: 'ASYNC' | 'SYNC';
  deleteStepType?: 'S3' | 'EFS';
  overwriteExisting?: 'TRUE' | 'FALSE';
  maxFileSize?: string;
  quarantineBucket?: string;
};

interface Server {
  ServerId: string;
  Domain: string;
  EndpointType: string;
  State: string;
  Tags?: Array<{ Key: string; Value: string }>;
  WorkflowDetails?: {
    OnUpload?: Array<{
      WorkflowId: string;
      ExecutionRole: string;
    }>;
  };
}

interface Workflow {
  WorkflowId: string;
  Description?: string;
  Steps?: Array<{
    Type: string;
    CopyStepDetails?: any;
    TagStepDetails?: any;
    CustomStepDetails?: any;
    DeleteStepDetails?: any;
  }>;
  OnExceptionSteps?: Array<{
    Type: string;
  }>;
}

interface Role {
  roleName: string;
  arn: string;
  description?: string;
}

interface LambdaFunction {
  functionName: string;
  functionArn: string;
  description: string;
  runtime: string;
  lastModified: string;
}

interface S3Bucket {
  name: string;
  creationDate: string;
}

// Helper function to generate unique IDs
function newId(): string {
  return Math.random().toString(36).slice(2, 9);
}

// Get server display name from tags or fallback to server ID
function getServerDisplayName(server: Server): string {
  const nameTag = server.Tags?.find(tag => tag.Key === 'Name');
  return nameTag?.Value || server.ServerId;
}

// Get server status display
function getServerStatus(server: Server): string {
  return server.State === 'ONLINE' ? 'Online' : 'Offline';
}

export default function WorkflowManagerNew() {
  // Data state
  const [servers, setServers] = useState<Server[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [lambdaFunctions, setLambdaFunctions] = useState<LambdaFunction[]>([]);
  const [s3Buckets, setS3Buckets] = useState<S3Bucket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selected items
  const [selectedServerId, setSelectedServerId] = useState<string>('');
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const [selectedServer, setSelectedServer] = useState<Server | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);

  // Workflow builder state
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [steps, setSteps] = useState<UiStep[]>([]);
  const [executionRole, setExecutionRole] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // UI state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdWorkflowId, setCreatedWorkflowId] = useState<string>('');

  // Load servers
  const loadServers = useCallback(async () => {
    try {
      const response = await fetch(workflowsApiUrl('servers'));
      if (!response.ok) throw new Error('Failed to load servers');
      const data = await response.json();
      setServers(data.servers || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load servers');
    }
  }, []);

  // Load server details (including attached workflows)
  const loadServer = useCallback(async (serverId: string) => {
    try {
      const response = await fetch(workflowsApiUrl(`servers?serverId=${serverId}`));
      if (!response.ok) throw new Error('Failed to load server details');
      const data = await response.json();
      setSelectedServer(data.server);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load server details');
    }
  }, []);

  // Load workflows
  const loadWorkflows = useCallback(async () => {
    try {
      const response = await fetch(workflowsApiUrl('workflows'));
      if (!response.ok) throw new Error('Failed to load workflows');
      const data = await response.json();
      setWorkflows(data.workflows || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflows');
    }
  }, []);

  // Load roles
  const loadRoles = useCallback(async () => {
    try {
      const response = await fetch(workflowsApiUrl('roles'));
      if (!response.ok) throw new Error('Failed to load roles');
      const data = await response.json();
      setRoles(data.roles || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roles');
    }
  }, []);

  // Load Lambda functions
  const loadLambdaFunctions = useCallback(async () => {
    try {
      const response = await fetch(workflowsApiUrl('lambda-functions'));
      if (!response.ok) throw new Error('Failed to load Lambda functions');
      const data = await response.json();
      setLambdaFunctions(data.functions || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Lambda functions');
    }
  }, []);

  // Load S3 buckets
  const loadS3Buckets = useCallback(async () => {
    try {
      const response = await fetch(workflowsApiUrl('s3-buckets'));
      if (!response.ok) throw new Error('Failed to load S3 buckets');
      const data = await response.json();
      setS3Buckets(data.buckets || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load S3 buckets');
    }
  }, []);

  // Load workflow details
  const loadWorkflow = useCallback(async (workflowId: string) => {
    try {
      const response = await fetch(workflowsApiUrl(`workflows/${workflowId}`));
      if (!response.ok) throw new Error('Failed to load workflow details');
      const data = await response.json();
      setSelectedWorkflow(data.workflow);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflow details');
    }
  }, []);

  // Load all data on component mount
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadServers(),
          loadWorkflows(),
          loadRoles(),
          loadLambdaFunctions(),
          loadS3Buckets(),
        ]);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [loadServers, loadWorkflows, loadRoles, loadLambdaFunctions, loadS3Buckets]);

  // Load server details when server is selected
  useEffect(() => {
    if (selectedServerId) {
      loadServer(selectedServerId);
    }
  }, [selectedServerId, loadServer]);

  // Load workflow details when workflow is selected
  useEffect(() => {
    if (selectedWorkflowId) {
      loadWorkflow(selectedWorkflowId);
    }
  }, [selectedWorkflowId, loadWorkflow]);

  // Add step
  const addStep = (type: StepType, category: 'nominal' | 'exception') => {
    const newStep: UiStep = {
      id: newId(),
      type,
      category,
    };
    setSteps(prev => [...prev, newStep]);
  };

  // Update step
  const updateStep = (id: string, updates: Partial<UiStep>) => {
    setSteps(prev => prev.map(s => (s.id === id ? { ...s, ...updates } : s)));
  };

  // Remove step
  const removeStep = (id: string) => {
    setSteps(prev => prev.filter(s => s.id !== id));
  };

  // Add tag to step
  const addTagToStep = (stepId: string) => {
    const step = steps.find(s => s.id === stepId);
    if (step) {
      const newTag = { key: '', value: '' };
      updateStep(stepId, {
        tags: [...(step.tags || []), newTag],
      });
    }
  };

  // Update tag in step
  const updateTagInStep = (stepId: string, tagIndex: number, key: string, value: string) => {
    const step = steps.find(s => s.id === stepId);
    if (step && step.tags) {
      const updatedTags = [...step.tags];
      updatedTags[tagIndex] = { key, value };
      updateStep(stepId, { tags: updatedTags });
    }
  };

  // Remove tag from step
  const removeTagFromStep = (stepId: string, tagIndex: number) => {
    const step = steps.find(s => s.id === stepId);
    if (step && step.tags) {
      const updatedTags = step.tags.filter((_, index) => index !== tagIndex);
      updateStep(stepId, { tags: updatedTags });
    }
  };

  // Create workflow
  const createWorkflow = async () => {
    if (!workflowName.trim() || steps.length === 0) return;

    setIsCreating(true);
    try {
      const response = await fetch(workflowsApiUrl('create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workflowId: workflowName,
          description: workflowDescription,
          steps: steps.filter(s => s.category === 'nominal'),
          onExceptionSteps: steps.filter(s => s.category === 'exception'),
          tags: [{ key: 'Name', value: workflowName }],
        }),
      });

      if (!response.ok) throw new Error('Failed to create workflow');

      const data = await response.json();
      setCreatedWorkflowId(data.workflowId);
      setShowCreateModal(false);
      setShowSuccessModal(true);
      await loadWorkflows();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create workflow');
    } finally {
      setIsCreating(false);
    }
  };

  // Attach workflow to server
  const attachWorkflow = async () => {
    const workflowId = createdWorkflowId || selectedWorkflowId;
    if (!selectedServerId || !workflowId || !executionRole) return;

    setIsCreating(true);
    try {
      const response = await fetch(workflowsApiUrl('attach'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serverId: selectedServerId,
          workflowId,
          executionRole,
        }),
      });

      if (!response.ok) throw new Error('Failed to attach workflow');

      await loadServer(selectedServerId);
      setShowAttachModal(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to attach workflow');
    } finally {
      setIsCreating(false);
    }
  };

  // Delete workflow
  const deleteWorkflow = async (workflowId: string) => {
    try {
      const response = await fetch(workflowsApiUrl(`workflows/${workflowId}`), {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete workflow');

      await loadWorkflows();
      if (selectedWorkflowId === workflowId) {
        setSelectedWorkflowId('');
        setSelectedWorkflow(null);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete workflow');
    }
  };

  // Reset form
  const resetForm = () => {
    setWorkflowName('');
    setWorkflowDescription('');
    setSteps([]);
    setExecutionRole('');
    setError(null);
  };

  // Handle create and attach
  const handleCreateAndAttach = async () => {
    await createWorkflow();
    if (createdWorkflowId) {
      setShowAttachModal(true);
    }
  };

  // Handle success modal actions
  const handleCreateAdditional = () => {
    setShowSuccessModal(false);
    setCreatedWorkflowId('');
    resetForm();
    setShowCreateModal(true);
  };

  const handleExitToDashboard = () => {
    setShowSuccessModal(false);
    setCreatedWorkflowId('');
    resetForm();
    // Navigate to dashboard - you might want to use useNavigate here
    window.location.href = '/';
  };

  // Render step configuration
  const renderStepConfiguration = (step: UiStep) => {
    switch (step.type) {
      case 'COPY':
        return (
          <div className='space-y-3 p-3 bg-white/5 border border-white/10 rounded-lg'>
            <div>
              <label className='block text-sm font-medium text-white mb-1'>Step Name</label>
              <input
                type='text'
                value={step.name || ''}
                onChange={e => updateStep(step.id, { name: e.target.value })}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                placeholder='Copy Step'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-white mb-1'>
                Destination Bucket
              </label>
              <input
                type='text'
                value={step.destinationBucket || ''}
                onChange={e => updateStep(step.id, { destinationBucket: e.target.value })}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                placeholder='my-bucket'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-white mb-1'>Destination Key</label>
              <input
                type='text'
                value={step.destinationKey || ''}
                onChange={e => updateStep(step.id, { destinationKey: e.target.value })}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                placeholder='path/to/file.txt'
              />
            </div>
          </div>
        );

      case 'TAG':
        return (
          <div className='space-y-3 p-3 bg-white/5 border border-white/10 rounded-lg'>
            <div>
              <label className='block text-sm font-medium text-white mb-1'>Step Name</label>
              <input
                type='text'
                value={step.name || ''}
                onChange={e => updateStep(step.id, { name: e.target.value })}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                placeholder='Tag Step'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-white mb-1'>Tags</label>
              <div className='space-y-2'>
                {(step.tags || []).map((tag, index) => (
                  <div key={index} className='flex gap-2'>
                    <input
                      type='text'
                      value={tag.key}
                      onChange={e => updateTagInStep(step.id, index, e.target.value, tag.value)}
                      className='flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                      placeholder='Key'
                    />
                    <input
                      type='text'
                      value={tag.value}
                      onChange={e => updateTagInStep(step.id, index, tag.key, e.target.value)}
                      className='flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                      placeholder='Value'
                    />
                    <button
                      onClick={() => removeTagFromStep(step.id, index)}
                      className='px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg'
                    >
                      <Trash2 className='h-4 w-4' />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addTagToStep(step.id)}
                  className='w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg'
                >
                  Add Tag
                </button>
              </div>
            </div>
          </div>
        );

      case 'CUSTOM':
        return (
          <div className='space-y-3 p-3 bg-white/5 border border-white/10 rounded-lg'>
            <div>
              <label className='block text-sm font-medium text-white mb-1'>Step Name</label>
              <input
                type='text'
                value={step.name || ''}
                onChange={e => updateStep(step.id, { name: e.target.value })}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                placeholder='Custom Step'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-white mb-1'>Lambda Function</label>
              <select
                value={step.functionArn || ''}
                onChange={e => updateStep(step.id, { functionArn: e.target.value })}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
              >
                <option value=''>Select Lambda function</option>
                {lambdaFunctions.map(func => (
                  <option key={func.functionArn} value={func.functionArn}>
                    {func.functionName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'DELETE':
        return (
          <div className='space-y-3 p-3 bg-white/5 border border-white/10 rounded-lg'>
            <div>
              <label className='block text-sm font-medium text-white mb-1'>Step Name</label>
              <input
                type='text'
                value={step.name || ''}
                onChange={e => updateStep(step.id, { name: e.target.value })}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                placeholder='Delete Step'
              />
            </div>
          </div>
        );

      case 'DECRYPT':
        return (
          <div className='space-y-3 p-3 bg-white/5 border border-white/10 rounded-lg'>
            <div>
              <label className='block text-sm font-medium text-white mb-1'>Step Name</label>
              <input
                type='text'
                value={step.name || ''}
                onChange={e => updateStep(step.id, { name: e.target.value })}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                placeholder='Decrypt Step'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-white mb-1'>
                Destination Bucket
              </label>
              <input
                type='text'
                value={step.destinationBucket || ''}
                onChange={e => updateStep(step.id, { destinationBucket: e.target.value })}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                placeholder='my-bucket'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-white mb-1'>Destination Key</label>
              <input
                type='text'
                value={step.destinationKey || ''}
                onChange={e => updateStep(step.id, { destinationKey: e.target.value })}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                placeholder='path/to/file.txt'
              />
            </div>
          </div>
        );

      default:
        return (
          <div className='p-3 bg-white/5 border border-white/10 rounded-lg'>
            <p className='text-slate-400'>Unknown step type: {step.type}</p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <RefreshCw className='h-8 w-8 animate-spin text-blue-400 mx-auto mb-4' />
          <p className='text-slate-400'>Loading workflow data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-white'>Workflow Management</h1>
          <p className='text-slate-400'>Create and manage file processing workflows</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className='bg-blue-600 hover:bg-blue-700 text-white'
        >
          <Plus className='h-4 w-4 mr-2' />
          Create Workflow
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className='bg-red-900/20 border border-red-500/50 rounded-lg p-4'>
          <div className='flex items-center gap-2'>
            <AlertCircle className='h-5 w-5 text-red-400' />
            <p className='text-red-400'>{error}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Servers */}
        <Card className='p-6'>
          <div className='flex items-center gap-2 mb-4'>
            <Server className='h-5 w-5 text-blue-400' />
            <h2 className='text-lg font-semibold text-white'>Transfer Servers</h2>
          </div>

          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-white mb-2'>Select Server</label>
              <select
                value={selectedServerId}
                onChange={e => setSelectedServerId(e.target.value)}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
              >
                <option value=''>Choose a server...</option>
                {servers.map(server => (
                  <option key={server.ServerId} value={server.ServerId}>
                    {getServerDisplayName(server)} ({getServerStatus(server)})
                  </option>
                ))}
              </select>
            </div>

            {selectedServer && (
              <div className='bg-slate-800/50 rounded-lg p-4'>
                <h3 className='font-medium text-white mb-2'>Server Details</h3>
                <div className='space-y-2 text-sm'>
                  <div className='flex justify-between'>
                    <span className='text-slate-400'>Status:</span>
                    <span className='text-green-400'>{getServerStatus(selectedServer)}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-slate-400'>Domain:</span>
                    <span className='text-white'>{selectedServer.Domain}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-slate-400'>Type:</span>
                    <span className='text-white'>{selectedServer.EndpointType}</span>
                  </div>
                </div>

                {selectedServer.WorkflowDetails?.OnUpload &&
                  selectedServer.WorkflowDetails.OnUpload.length > 0 && (
                    <div className='mt-4'>
                      <h4 className='font-medium text-white mb-2'>Attached Workflows</h4>
                      <div className='space-y-2'>
                        {selectedServer.WorkflowDetails.OnUpload.map((workflow, index) => {
                          // Find the workflow name from the workflows list
                          const workflowInfo = workflows.find(
                            w => w.WorkflowId === workflow.WorkflowId
                          );
                          const displayName = workflowInfo?.Description || workflow.WorkflowId;
                          return (
                            <div
                              key={index}
                              className='flex items-center justify-between bg-slate-700/50 rounded p-2'
                            >
                              <div>
                                <span className='text-sm text-white'>{displayName}</span>
                                {workflowInfo?.Description && (
                                  <div className='text-xs text-slate-400'>
                                    {workflow.WorkflowId}
                                  </div>
                                )}
                              </div>
                              <CheckCircle2 className='h-4 w-4 text-green-400' />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>
        </Card>

        {/* Workflows */}
        <Card className='p-6'>
          <div className='flex items-center gap-2 mb-4'>
            <Workflow className='h-5 w-5 text-blue-400' />
            <h2 className='text-lg font-semibold text-white'>Available Workflows</h2>
          </div>

          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-white mb-2'>Select Workflow</label>
              <select
                value={selectedWorkflowId}
                onChange={e => setSelectedWorkflowId(e.target.value)}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
              >
                <option value=''>Choose a workflow...</option>
                {workflows.map(workflow => (
                  <option key={workflow.WorkflowId} value={workflow.WorkflowId}>
                    {workflow.Description
                      ? `${workflow.Description} (${workflow.WorkflowId})`
                      : workflow.WorkflowId}
                  </option>
                ))}
              </select>
            </div>

            {selectedWorkflow && (
              <div className='bg-slate-800/50 rounded-lg p-4'>
                <div className='flex items-center justify-between mb-2'>
                  <h3 className='font-medium text-white'>Workflow Details</h3>
                  <div className='flex items-center gap-2'>
                    <button
                      onClick={() => {
                        setCreatedWorkflowId(selectedWorkflow.WorkflowId);
                        setShowAttachModal(true);
                      }}
                      className='px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors'
                      title='Attach to server'
                    >
                      <ArrowRight className='h-3 w-3 mr-1' />
                      Attach
                    </button>
                    <button
                      onClick={() => deleteWorkflow(selectedWorkflow.WorkflowId)}
                      className='text-red-400 hover:text-red-300'
                      title='Delete workflow'
                    >
                      <Trash2 className='h-4 w-4' />
                    </button>
                  </div>
                </div>
                <div className='space-y-2 text-sm'>
                  <div className='flex justify-between'>
                    <span className='text-slate-400'>ID:</span>
                    <span className='text-white font-mono'>{selectedWorkflow.WorkflowId}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-slate-400'>Description:</span>
                    <span className='text-white'>
                      {selectedWorkflow.Description || 'No description'}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-slate-400'>Steps:</span>
                    <span className='text-white'>{selectedWorkflow.Steps?.length || 0}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-slate-400'>Exception Steps:</span>
                    <span className='text-white'>
                      {selectedWorkflow.OnExceptionSteps?.length || 0}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Create Workflow Modal */}
      {showCreateModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <div className='bg-slate-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto'>
            <div className='p-6 border-b border-slate-700'>
              <div className='flex items-center justify-between'>
                <h3 className='text-lg font-medium text-white'>Create New Workflow</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className='text-slate-400 hover:text-white'
                >
                  <X className='h-5 w-5' />
                </button>
              </div>
            </div>

            <div className='p-6 space-y-6'>
              {/* Workflow Details */}
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-white mb-2'>Workflow Name</label>
                  <input
                    type='text'
                    value={workflowName}
                    onChange={e => setWorkflowName(e.target.value)}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    placeholder='my-workflow'
                    required
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-white mb-2'>Description</label>
                  <input
                    type='text'
                    value={workflowDescription}
                    onChange={e => setWorkflowDescription(e.target.value)}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    placeholder='Process uploaded files'
                  />
                </div>
              </div>

              {/* Workflow Steps */}
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <h3 className='text-lg font-semibold text-slate-100'>Workflow Steps</h3>
                  <div className='grid grid-cols-2 sm:grid-cols-5 gap-2'>
                    <Button variant='soft' onClick={() => addStep('COPY', 'nominal')}>
                      <Copy className='h-4 w-4 mr-1' />
                      Copy
                    </Button>
                    <Button variant='soft' onClick={() => addStep('TAG', 'nominal')}>
                      <Tag className='h-4 w-4 mr-1' />
                      Tag
                    </Button>
                    <Button variant='soft' onClick={() => addStep('CUSTOM', 'nominal')}>
                      <Zap className='h-4 w-4 mr-1' />
                      Custom
                    </Button>
                    <Button variant='soft' onClick={() => addStep('DELETE', 'nominal')}>
                      <Trash2 className='h-4 w-4 mr-1' />
                      Delete
                    </Button>
                    <Button variant='soft' onClick={() => addStep('DECRYPT', 'nominal')}>
                      <FileText className='h-4 w-4 mr-1' />
                      Decrypt
                    </Button>
                  </div>
                </div>

                <div className='space-y-3'>
                  {steps
                    .filter(step => step.category === 'nominal')
                    .map((step, index) => (
                      <div key={step.id} className='border border-slate-600 rounded-lg'>
                        <div className='flex items-center justify-between p-3 bg-slate-700/50'>
                          <div className='flex items-center gap-2'>
                            <span className='text-sm font-medium text-white'>
                              Step {index + 1}: {step.type}
                            </span>
                          </div>
                          <button
                            onClick={() => removeStep(step.id)}
                            className='text-red-400 hover:text-red-300'
                          >
                            <Trash2 className='h-4 w-4' />
                          </button>
                        </div>
                        {renderStepConfiguration(step)}
                      </div>
                    ))}
                </div>

                {/* Exception Handlers */}
                <div className='flex items-center justify-between'>
                  <h3 className='text-lg font-semibold text-slate-100'>Exception Handlers</h3>
                  <div className='grid grid-cols-2 sm:grid-cols-5 gap-2'>
                    <Button variant='soft' onClick={() => addStep('COPY', 'exception')}>
                      <Copy className='h-4 w-4 mr-1' />
                      Copy
                    </Button>
                    <Button variant='soft' onClick={() => addStep('TAG', 'exception')}>
                      <Tag className='h-4 w-4 mr-1' />
                      Tag
                    </Button>
                    <Button variant='soft' onClick={() => addStep('CUSTOM', 'exception')}>
                      <Zap className='h-4 w-4 mr-1' />
                      Custom
                    </Button>
                    <Button variant='soft' onClick={() => addStep('DELETE', 'exception')}>
                      <Trash2 className='h-4 w-4 mr-1' />
                      Delete
                    </Button>
                    <Button variant='soft' onClick={() => addStep('DECRYPT', 'exception')}>
                      <FileText className='h-4 w-4 mr-1' />
                      Decrypt
                    </Button>
                  </div>
                </div>

                <div className='space-y-3'>
                  {steps
                    .filter(step => step.category === 'exception')
                    .map((step, index) => (
                      <div key={step.id} className='border border-slate-600 rounded-lg'>
                        <div className='flex items-center justify-between p-3 bg-slate-700/50'>
                          <div className='flex items-center gap-2'>
                            <span className='text-sm font-medium text-white'>
                              Exception {index + 1}: {step.type}
                            </span>
                          </div>
                          <button
                            onClick={() => removeStep(step.id)}
                            className='text-red-400 hover:text-red-300'
                          >
                            <Trash2 className='h-4 w-4' />
                          </button>
                        </div>
                        {renderStepConfiguration(step)}
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className='p-6 border-t border-slate-700'>
              <div className='flex items-center justify-end gap-3'>
                <Button
                  onClick={() => setShowCreateModal(false)}
                  className='bg-slate-600 hover:bg-slate-700 text-white'
                  variant='soft'
                >
                  Cancel
                </Button>
                <Button
                  onClick={createWorkflow}
                  disabled={!workflowName.trim() || steps.length === 0 || isCreating}
                  className='bg-blue-600 hover:bg-blue-700 text-white'
                >
                  {isCreating ? (
                    <>
                      <RefreshCw className='h-4 w-4 mr-2 animate-spin' />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className='h-4 w-4 mr-2' />
                      Create Workflow
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleCreateAndAttach}
                  disabled={!workflowName.trim() || steps.length === 0 || isCreating}
                  className='bg-purple-600 hover:bg-purple-700 text-white'
                >
                  {isCreating ? (
                    <>
                      <RefreshCw className='h-4 w-4 mr-2 animate-spin' />
                      Creating...
                    </>
                  ) : (
                    <>
                      <ArrowRight className='h-4 w-4 mr-2' />
                      Create & Attach
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attach Workflow Modal */}
      {showAttachModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <div className='bg-slate-800 rounded-lg max-w-2xl w-full'>
            <div className='p-6 border-b border-slate-700'>
              <h3 className='text-lg font-medium text-white'>Attach Workflow to Server</h3>
              <p className='text-slate-400'>Connect this workflow to a Transfer Family server</p>
            </div>

            <div className='p-6 space-y-4'>
              <div>
                <label className='block text-sm font-medium text-white mb-2'>Select Server</label>
                <select
                  value={selectedServerId}
                  onChange={e => setSelectedServerId(e.target.value)}
                  className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                >
                  <option value=''>Choose a server...</option>
                  {servers.map(server => (
                    <option key={server.ServerId} value={server.ServerId}>
                      {getServerDisplayName(server)} ({getServerStatus(server)})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-white mb-2'>Execution Role</label>
                <select
                  value={executionRole}
                  onChange={e => setExecutionRole(e.target.value)}
                  className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                >
                  <option value=''>Choose an execution role...</option>
                  {roles.map(role => (
                    <option key={role.arn} value={role.arn}>
                      {role.roleName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className='p-6 border-t border-slate-700'>
              <div className='flex items-center justify-end gap-3'>
                <Button
                  onClick={() => setShowAttachModal(false)}
                  className='bg-slate-600 hover:bg-slate-700 text-white'
                  variant='soft'
                >
                  Cancel
                </Button>
                <Button
                  onClick={attachWorkflow}
                  disabled={!selectedServerId || !executionRole || isCreating}
                  className='bg-purple-600 hover:bg-purple-700 text-white'
                >
                  {isCreating ? (
                    <>
                      <RefreshCw className='h-4 w-4 mr-2 animate-spin' />
                      Attaching...
                    </>
                  ) : (
                    <>
                      <ArrowRight className='h-4 w-4 mr-2' />
                      Attach Workflow
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <div className='bg-slate-800 rounded-lg max-w-md w-full'>
            <div className='p-6 text-center'>
              <CheckCircle2 className='h-12 w-12 text-green-400 mx-auto mb-4' />
              <h3 className='text-lg font-medium text-white mb-2'>
                Workflow Created Successfully!
              </h3>
              <p className='text-slate-400 mb-6'>
                Your workflow "{createdWorkflowId}" has been created and is ready to use.
              </p>

              <div className='space-y-3'>
                <Button
                  onClick={handleCreateAdditional}
                  className='w-full bg-blue-600 hover:bg-blue-700 text-white'
                >
                  <Plus className='h-4 w-4 mr-2' />
                  Create Additional Workflow
                </Button>
                <Button
                  onClick={handleExitToDashboard}
                  className='w-full bg-slate-600 hover:bg-slate-700 text-white'
                  variant='soft'
                >
                  <ArrowRight className='h-4 w-4 mr-2' />
                  Exit to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
